<template>
  <div
    :class="[
      'inno-add-to-cart',
      {
        'inno-add-to-cart--wide': wide,
      },
    ]"
  >
    <InnoCartInput
      v-model.number="quantity"
      :name="name"
      type="number"
      :aria-label="$t('ui_kit.labels.product_quantity')"
      :disabled="isDisabled"
      :max="maxQuantity"
      :min="minQuantity"
      :size="size"
      single-line-message
      center
      :error="!isValid || error"
      :message="message"
      :show-empty-details="showEmptyDetails"
      :readonly="readonly"
      @blur="onFocusOut"
      @input="hideButton ? onChange() : ''"
    >
      <template #prepend>
        <template v-if="quantityOperators">
          <button
            type="button"
            class="flex size-12 h-full items-center justify-center text-[--color-body-text] disabled:text-[--color-additional-300]"
            :class="{ 'hover:text-[--color-link]': !disabled }"
            :aria-label="$t('shared.catalog.product_card.decrement', { productname: name })"
            :disabled="disabled"
            @click="decrementQuantity()"
          >
            <VcIcon name="cart-decrement" size="xs" class="relative size-12 mr-1" />
          </button>
        </template>
      </template>

      <template #append>
        <template v-if="quantityOperators">
          <button
            type="button"
            class="flex size-12 h-full items-center justify-center text-[--color-body-text] disabled:text-[--color-additional-300]"
            :class="{ 'hover:text-[--color-link]': !disabled }"
            :aria-label="$t('shared.catalog.product_card.increment', { productname: name })"
            :disabled="disabled"
            @click="incrementQuantity()"
          >
            <VcIcon name="cart-increment" size="xs" class="relative size-12 ml-1" />
          </button>
        </template>

        <!-- BB2B-119 errorMessage causes add/update issue when signed in -->
        <VcButton
          v-if="!hideButton"
          class="!min-w-[100px] w-full ml-2 md:ml-4 rounded-full px-4"
          :variant="!countInCart ? 'solid' : 'outline'"
          color="primary"
          :loading="loading"
          :disabled="disabled || !!errorMessage"
          :title="buttonText"
          size="md"
          truncate
          @click.stop="onClick()"
        >
          {{ buttonText }}
        </VcButton>
      </template>
    </InnoCartInput>
  </div>

  <div v-if="$slots.default" class="vc-add-to-cart__badges">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { toRefs } from "@vueuse/core";
import { debounce } from "lodash";
import { useField } from "vee-validate";
import { computed, onMounted, ref, toRef, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { LINE_ITEM_QUANTITY_LIMIT } from "@/core/constants";
import { useQuantityValidationSchema } from "@/ui-kit/composables";
//import { AddToCartModeType } from "@/ui-kit/enums";

interface IEmits {
  (event: "update:modelValue", value: number): void;
  (event: "update:cartItemQuantity", quantity: number): void;
  (event: "update:validation", value: { isValid: true } | { isValid: false; errorMessage: string }): void;
}

interface IProps {
  name?: string;
  modelValue?: number;
  loading?: boolean;
  disabled?: boolean;
  minQuantity?: number;
  maxQuantity?: number;
  packSize?: number;
  countInCart?: number;
  availableQuantity?: number;
  isActive?: boolean;
  isAvailable?: boolean;
  isBuyable?: boolean;
  isInStock?: boolean;
  message?: string;
  showEmptyDetails?: boolean;
  error?: boolean;
  hideButton?: boolean;
  readonly?: boolean;
  validateOnMount?: boolean;
  size?: "xs" | "sm" | "md";
  quantityOperators?: boolean;
  wide?: boolean;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  validateOnMount: true,
  size: "md",
  packSize: 1,
  quantityOperators: false,
  wide: false,
});

const { t } = useI18n();

const isValid = ref(true);

const {
  disabled,
  isInStock,
  minQuantity,
  maxQuantity,
  availableQuantity,
  isActive,
  isAvailable,
  isBuyable,
  countInCart,
} = toRefs(props);

//const mode = computed(() => (countInCart.value ? AddToCartModeType.Update : AddToCartModeType.Add));

const modelValue = toRef(props, "modelValue");
const pendingQuantity = ref<number | null>(null);

const defaultQuantity = computed<number>(() => props.packSize ?? 1);

const quantity = ref<number | undefined>(
  !disabled.value ? countInCart.value || (props.packSize ? defaultQuantity.value : maxQuantity.value) : undefined,
);

const buttonText = computed<string>(() =>
  countInCart.value ? t("common.buttons.update_cart") : t("common.buttons.add_to_cart"),
);

const { quantitySchema } = useQuantityValidationSchema({
  maxQuantity,
  //availableQuantity, Innovadis custom: ignore available quantity for validation, backorders are allowed
});

const rules = computed(() => toTypedSchema(quantitySchema.value));
const isDisabled = computed(
  () => !isValid.value || disabled.value || !isAvailable.value || !isBuyable.value || !isActive?.value, // Innovadis custom: no stock check: || !isInStock.value
);

const {
  errorMessage,
  validate,
  setValue,
  value: fieldValue,
} = useField("quantity", rules, {
  initialValue: quantity.value,
});

async function validateFields(): Promise<void> {
  if (isInStock.value && !disabled.value && isAvailable.value && isBuyable.value && isActive?.value) {
    const { valid } = await validate();

    isValid.value = valid;
    if (!valid && errorMessage.value) {
      emit("update:validation", { isValid: false, errorMessage: errorMessage.value });
    } else {
      emit("update:validation", { isValid: true });
    }
  } else {
    isValid.value = true;
    emit("update:validation", { isValid: true });
  }
}

const handleChange = debounce(async () => {
  setValue(quantity.value);

  let newQuantity = Number(quantity.value);

  if (
    isNaN(newQuantity) ||
    newQuantity < 1 ||
    newQuantity === modelValue.value ||
    pendingQuantity.value === newQuantity
  ) {
    return;
  }
  await validateFields();

  const packSize = props.packSize;
  newQuantity = Math.ceil(newQuantity / packSize) * packSize;

  quantity.value = newQuantity;

  emit("update:modelValue", newQuantity);

  pendingQuantity.value = newQuantity;
}, 2000);

function onChange() {
  if (quantity.value && quantity.value > LINE_ITEM_QUANTITY_LIMIT) {
    quantity.value = Number(quantity.value.toString().slice(0, -1));
  }
  void handleChange();
}

function onFocusOut() {
  const newQuantity = Number(quantity.value);
  if (isNaN(newQuantity) || newQuantity < 1) {
    quantity.value = modelValue.value;
  }
}

async function onClick() {
  setValue(quantity.value);

  let newQuantity = Number(quantity.value);

  await validateFields();

  const packSize = props.packSize;
  newQuantity = Math.ceil(newQuantity / packSize) * packSize;

  quantity.value = newQuantity;

  emit("update:modelValue", newQuantity);
  emit("update:cartItemQuantity", newQuantity!);
}

function decrementQuantity() {
  if (quantity.value === undefined || quantity.value <= props.packSize) {
    quantity.value = props.packSize;
  } else {
    quantity.value -= props.packSize;
  }

  emit("update:modelValue", quantity.value);
  setValue(quantity.value);
}

function incrementQuantity() {
  if (quantity.value === undefined) {
    quantity.value = props.packSize;
  } else if (quantity.value + props.packSize) {
    quantity.value += props.packSize;
  } else {
    quantity.value = props.maxQuantity!;
  }

  emit("update:modelValue", quantity.value);
  setValue(quantity.value);
}

watchEffect(() => {
  pendingQuantity.value = null;
});

onMounted(async () => {
  if (props.validateOnMount) {
    await validateFields();
  }
});

watchEffect(async () => {
  if (quantity.value !== fieldValue.value) {
    setValue(quantity.value);
    await validateFields();
  }
});
</script>

<style lang="scss">
.inno-add-to-cart {
  @apply max-w-[260px] w-full;

  &--wide {
    @apply w-full;
  }

  .vc-input__container {
    @apply border-0;
  }
}

.vc-add-to-cart {
  $self: &;
  $hideButton: &;

  @apply @container flex-none;

  &--hide-button {
    $hideButton: &;
  }

  &__icon-button.vc-button {
    @container (width > theme("containers.xxs")) {
      @apply hidden;
    }
  }

  &__text-button.vc-button {
    @apply hidden;

    @container (width > theme("containers.xxs")) {
      @apply block;
    }
  }

  &__badges {
    @apply mt-2 flex flex-wrap gap-x-1.5 gap-y-0.5 empty:hidden;
  }

  @at-root .vc-product-card {
    #{$self} {
      grid-area: add-to-cart;
    }

    &--view-mode {
      &--grid {
        #{$self} {
          @apply mt-3 order-7;
        }
      }

      &--list {
        #{$self} {
          @apply mt-3;

          @container (min-width: theme("containers.sm")) {
            @apply w-72;
          }

          @container (min-width: theme("containers.xl")) {
            @apply mt-0 ms-3 w-44;
          }

          @container (min-width: theme("containers.4xl")) {
            @apply mt-0 ms-3 w-60;
          }
        }
      }

      &--item {
        #{$hideButton} {
          @apply mt-3 w-[5rem] self-start;

          @container (min-width: theme("containers.xl")) {
            @apply w-[6.75rem] self-center;
          }

          @container (min-width: theme("containers.2xl")) {
            @apply mt-0 ms-3 w-[5.5rem];
          }
        }
      }
    }
  }
}
</style>
